import { buildScoreSharePayload, buildSocialSharePayload, getSocialFormat } from './social-share.js';
import { avatarInitial, sanitizeAvatarEmoji, sanitizeAvatarUrl } from './avatar.js';
import { tr } from '../services/i18n.js';

export function buildSocialImageLayout(format) {
  const f = getSocialFormat(format?.id || format);
  const margin = Math.round(Math.min(f.width, f.height) * 0.07);
  const footerHeight = Math.round(f.height * (f.id === 'og' ? 0.13 : 0.11));
  const frameGap = Math.round(margin * 0.35);
  const headerHeight = Math.round(f.height * ({ story: 0.26, og: 0.26, square: 0.34 }[f.id] || 0.34));
  const safeGap = Math.round(margin * 0.55);
  const frameAreaTop = headerHeight + safeGap;
  const frameAreaBottom = f.height - footerHeight - safeGap;
  const frameAreaHeight = Math.max(Math.round(f.height * 0.26), frameAreaBottom - frameAreaTop);
  const titleSize = Math.round(Math.min(f.width, f.height) * ({ story: 0.064, og: 0.082, square: 0.058 }[f.id] || 0.058));
  const bodySize = Math.round(Math.min(f.width, f.height) * ({ story: 0.033, og: 0.039, square: 0.03 }[f.id] || 0.03));
  return {
    id: f.id,
    width: f.width,
    height: f.height,
    margin,
    footerHeight,
    headerHeight,
    safeGap,
    frameGap,
    titleSize,
    bodySize,
    headerArea: {
      x: margin,
      y: margin,
      width: f.width - margin * 2,
      height: headerHeight - margin
    },
    frameArea: {
      x: margin,
      y: frameAreaTop,
      width: f.width - margin * 2,
      height: frameAreaHeight
    },
    footerArea: {
      x: 0,
      y: f.height - footerHeight,
      width: f.width,
      height: footerHeight
    }
  };
}

export function buildScoreRowTextLayout(ctx, layout, pointsText, area = layout.frameArea, { rowHeight = null } = {}) {
  const padX = Math.max(22, layout.frameGap * 1.05);
  const rankSize = Math.round(layout.titleSize * 0.66);
  const nameSize = Math.round(layout.bodySize * 1.16);
  const metaSize = Math.round(layout.bodySize * 0.78);
  const pointsSize = Math.round(layout.bodySize * 0.96);
  const avatarSize = Math.round(Math.min(
    rowHeight ? rowHeight * 0.58 : layout.bodySize * 2.55,
    layout.bodySize * 3.15
  ));
  const avatarGap = Math.max(14, Math.round(padX * 0.5));
  const rankX = area.x + padX;

  ctx.font = `900 ${rankSize}px Inter, Arial, sans-serif`;
  const rankW = Math.max(
    ctx.measureText('#1').width,
    ctx.measureText('#2').width,
    ctx.measureText('#3').width
  );
  const rankColumnW = rankW + padX * 1.25;

  ctx.font = `900 ${pointsSize}px Inter, Arial, sans-serif`;
  const pointsW = ctx.measureText(pointsText).width;
  const pointsX = area.x + area.width - padX - pointsW;
  const avatarX = rankX + rankColumnW;
  const nameX = avatarX + avatarSize + avatarGap;
  const nameMaxW = Math.max(80, pointsX - nameX - padX * 1.25);

  return {
    padX,
    rankSize,
    nameSize,
    metaSize,
    pointsSize,
    rankX,
    rankW,
    rankColumnW,
    avatarX,
    avatarSize,
    avatarGap,
    pointsX,
    pointsW,
    nameX,
    nameMaxW
  };
}

export async function generateSocialShareImage(debrief, { platform = 'x', format = null, url = window.location.href } = {}) {
  const payload = buildSocialSharePayload(debrief, { platform, url });
  const imageFormat = getSocialFormat(format || payload.format.id);
  const layout = buildSocialImageLayout(imageFormat);
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');

  drawBackground(ctx, layout);
  drawHeader(ctx, layout, payload);
  await drawFrames(ctx, layout, debrief?.ai_raw_response?.frame_previews || []);
  drawFooter(ctx, layout, payload);

  return canvas.toDataURL('image/png');
}

export async function generateScoreShareImage(scoreData, { platform = 'x', format = null, url = window.location.href } = {}) {
  const payload = buildScoreSharePayload(scoreData, { platform, url });
  const imageFormat = getSocialFormat(format || payload.format.id);
  const layout = buildSocialImageLayout(imageFormat);
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');

  drawBackground(ctx, layout);
  drawHeader(ctx, layout, payload);
  await drawScoreRows(ctx, layout, payload.rows || []);
  drawFooter(ctx, layout, payload);

  return canvas.toDataURL('image/png');
}

function drawBackground(ctx, layout) {
  const g = ctx.createLinearGradient(0, 0, layout.width, layout.height);
  g.addColorStop(0, '#f6faf2');
  g.addColorStop(0.55, '#e8f1e8');
  g.addColorStop(1, '#dbe8d7');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, layout.width, layout.height);

  ctx.strokeStyle = 'rgba(15,81,50,0.16)';
  ctx.lineWidth = Math.max(2, layout.width * 0.004);
  const pitchX = layout.margin * 0.7;
  const pitchY = Math.max(layout.frameArea.y - layout.safeGap * 0.45, layout.headerHeight);
  const pitchW = layout.width - pitchX * 2;
  const pitchH = Math.min(
    layout.footerArea.y - pitchY - layout.safeGap * 0.35,
    layout.frameArea.height + layout.safeGap * 1.15
  );
  ctx.strokeRect(pitchX, pitchY, pitchW, pitchH);
  ctx.beginPath();
  ctx.moveTo(layout.width / 2, pitchY);
  ctx.lineTo(layout.width / 2, pitchY + pitchH);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(layout.width / 2, pitchY + pitchH / 2, pitchW * 0.11, 0, Math.PI * 2);
  ctx.stroke();
}

function drawHeader(ctx, layout, payload) {
  const { x, y, width, height } = layout.headerArea;
  ctx.fillStyle = '#101815';
  const title = drawFittedWrappedText(ctx, {
    text: payload.imageTitle,
    x,
    y,
    maxWidth: width,
    maxHeight: Math.round(height * 0.58),
    fontWeight: 900,
    initialSize: layout.titleSize,
    minSize: Math.round(layout.titleSize * 0.72),
    lineHeightRatio: 1.03,
    maxLines: 2,
    color: '#101815'
  });

  ctx.fillStyle = '#0f5132';
  drawFittedWrappedText(ctx, {
    text: payload.imageSubtitle,
    x,
    y: title.bottom + Math.round(layout.bodySize * 0.55),
    maxWidth: width,
    maxHeight: Math.max(layout.bodySize * 1.5, layout.frameArea.y - title.bottom - layout.safeGap),
    fontWeight: 800,
    initialSize: layout.bodySize,
    minSize: Math.round(layout.bodySize * 0.82),
    lineHeightRatio: 1.28,
    maxLines: 2,
    color: '#0f5132'
  });
}

async function drawFrames(ctx, layout, frames) {
  const valid = frames.filter((src) => typeof src === 'string' && src.startsWith('data:image/')).slice(0, 2);
  const area = layout.frameArea;
  const cardW = valid.length > 1 ? (area.width - layout.frameGap) / 2 : area.width;
  const cards = valid.length ? valid : [null];
  for (let i = 0; i < cards.length; i += 1) {
    const x = area.x + i * (cardW + layout.frameGap);
    roundRect(ctx, x, area.y, cardW, area.height, 22);
    ctx.fillStyle = '#16201c';
    ctx.fill();
    if (!cards[i]) {
      ctx.fillStyle = '#d8e1d8';
      ctx.font = `800 ${layout.bodySize}px Inter, Arial, sans-serif`;
      ctx.fillText(tr('Images clés fooview', 'fooview key frames'), x + layout.frameGap, area.y + area.height / 2);
      continue;
    }
    try {
      const image = await loadImage(cards[i]);
      drawCoverImage(ctx, image, x, area.y, cardW, area.height);
    } catch {
      ctx.fillStyle = '#d8e1d8';
      ctx.font = `800 ${layout.bodySize}px Inter, Arial, sans-serif`;
      ctx.fillText(tr('Image indisponible', 'Image unavailable'), x + layout.frameGap, area.y + area.height / 2);
    }
  }
}

async function drawScoreRows(ctx, layout, rows) {
  const area = layout.frameArea;
  const count = Math.max(1, Math.min(3, rows.length || 1));
  const gap = layout.frameGap * 0.7;
  const rowH = (area.height - gap * (count - 1)) / count;

  const cards = rows.length ? rows.slice(0, 3) : [{ pseudo: tr('Premier score à venir', 'First score coming soon'), points: 0, level: 1 }];
  for (let index = 0; index < cards.length; index += 1) {
    const row = cards[index];
    const y = area.y + index * (rowH + gap);
    roundRect(ctx, area.x, y, area.width, rowH, 22);
    ctx.fillStyle = index === 0 ? '#101815' : 'rgba(16,24,21,0.88)';
    ctx.fill();

    const baselineName = y + rowH * 0.44;
    const baselineMeta = y + rowH * 0.70;
    const pointsText = `${row.points || 0} pts`;
    const textLayout = buildScoreRowTextLayout(ctx, layout, pointsText, area, { rowHeight: rowH });
    const avatarY = y + (rowH - textLayout.avatarSize) / 2;

    ctx.fillStyle = index === 0 ? '#c6ff4d' : '#f6faf2';
    ctx.font = `900 ${textLayout.rankSize}px Inter, Arial, sans-serif`;
    ctx.fillText(`#${index + 1}`, textLayout.rankX, y + rowH * 0.58);

    await drawScoreAvatar(ctx, row, {
      x: textLayout.avatarX,
      y: avatarY,
      size: textLayout.avatarSize,
      highlight: index === 0
    });

    ctx.fillStyle = '#ffffff';
    ctx.font = `900 ${textLayout.nameSize}px Inter, Arial, sans-serif`;
    ctx.fillText(truncateToWidth(ctx, row.pseudo || tr('Joueur', 'Player'), textLayout.nameMaxW), textLayout.nameX, baselineName);

    ctx.fillStyle = '#c6ff4d';
    ctx.font = `900 ${textLayout.pointsSize}px Inter, Arial, sans-serif`;
    ctx.fillText(pointsText, textLayout.pointsX, baselineName);

    ctx.fillStyle = '#d8e1d8';
    ctx.font = `700 ${textLayout.metaSize}px Inter, Arial, sans-serif`;
    ctx.fillText(
      truncateToWidth(ctx, `${tr('Niv.', 'Lvl.')} ${row.level || 1} · ${row.debriefs || 0} ${tr('analyses', 'analyses')} · ${row.quizzes || 0} quiz`, textLayout.nameMaxW),
      textLayout.nameX,
      baselineMeta
    );
  }
}

async function drawScoreAvatar(ctx, row, { x, y, size, highlight = false }) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = highlight ? '#c6ff4d' : '#eef6e8';
  ctx.fill();
  ctx.clip();

  const avatarUrl = sanitizeAvatarUrl(row?.avatar_url);
  if (avatarUrl) {
    try {
      const image = await loadImage(canvasAvatarUrl(avatarUrl), { crossOrigin: 'anonymous' });
      drawCoverImage(ctx, image, x, y, size, size);
      ctx.restore();
      drawAvatarRing(ctx, x, y, size, highlight);
      return;
    } catch {
      ctx.restore();
      drawAvatarFallback(ctx, row, { x, y, size, highlight });
      return;
    }
  }

  ctx.restore();
  drawAvatarFallback(ctx, row, { x, y, size, highlight });
}

function drawAvatarFallback(ctx, row, { x, y, size, highlight = false }) {
  const text = sanitizeAvatarEmoji(row?.avatar_emoji) || avatarInitial(row?.pseudo);
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = highlight ? '#c6ff4d' : '#eef6e8';
  ctx.fill();
  ctx.fillStyle = '#101815';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `900 ${Math.round(size * 0.46)}px Inter, Arial, sans-serif`;
  ctx.fillText(text, x + size / 2, y + size / 2 + size * 0.02, size * 0.78);
  ctx.restore();
  drawAvatarRing(ctx, x, y, size, highlight);
}

function drawAvatarRing(ctx, x, y, size, highlight = false) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.strokeStyle = highlight ? '#ffffff' : 'rgba(255,255,255,0.72)';
  ctx.lineWidth = Math.max(3, Math.round(size * 0.055));
  ctx.stroke();
  ctx.restore();
}

function canvasAvatarUrl(src) {
  if (!/^https?:\/\//i.test(src)) return src;
  try {
    const url = new URL(src);
    url.searchParams.set('canvas', '1');
    return url.toString();
  } catch {
    return src;
  }
}

function drawFooter(ctx, layout, payload) {
  const y = layout.footerArea.y;
  ctx.fillStyle = '#101815';
  ctx.fillRect(0, y, layout.width, layout.footerHeight);
  ctx.fillStyle = '#ffffff';
  ctx.font = `900 ${layout.bodySize * 1.1}px Inter, Arial, sans-serif`;
  ctx.fillText('fooview', layout.margin, y + layout.footerHeight * 0.45);
  ctx.fillStyle = '#c6ff4d';
  ctx.font = `700 ${layout.bodySize * 0.82}px Inter, Arial, sans-serif`;
  const footer = payload.competition || payload.platform.label;
  ctx.fillText(truncateToWidth(ctx, footer, layout.width - layout.margin * 2), layout.margin, y + layout.footerHeight * 0.72);
}

function drawFittedWrappedText(ctx, { text, x, y, maxWidth, maxHeight, fontWeight, initialSize, minSize, lineHeightRatio, maxLines, color }) {
  for (let size = initialSize; size >= minSize; size -= 2) {
    ctx.font = `${fontWeight} ${size}px Inter, Arial, sans-serif`;
    const lineHeight = size * lineHeightRatio;
    const lines = wrapText(ctx, text, maxWidth, maxLines);
    if (lines.length * lineHeight <= maxHeight || size <= minSize) {
      ctx.fillStyle = color;
      lines.forEach((line, index) => ctx.fillText(line, x, y + size + index * lineHeight));
      return { lines, size, lineHeight, bottom: y + lines.length * lineHeight };
    }
  }
  return { lines: [], size: minSize, lineHeight: minSize * lineHeightRatio, bottom: y };
}

function wrapText(ctx, text, maxWidth, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length >= maxLines) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (lines.length > maxLines) lines.length = maxLines;
  const usedWords = lines.join(' ').split(/\s+/).filter(Boolean).length;
  if (usedWords < words.length && lines.length) lines[lines.length - 1] = truncateToWidth(ctx, `${lines[lines.length - 1]}…`, maxWidth);
  return lines.map((entry) => truncateToWidth(ctx, entry, maxWidth));
}

function truncateToWidth(ctx, text, maxWidth) {
  const value = String(text || '');
  if (ctx.measureText(value).width <= maxWidth) return value;
  let clipped = value;
  while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1).trimEnd();
  }
  return `${clipped}…`;
}

function drawCoverImage(ctx, image, x, y, w, h) {
  const scale = Math.max(w / image.width, h / image.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (image.width - sw) / 2;
  const sy = (image.height - sh) / 2;
  ctx.save();
  roundRect(ctx, x, y, w, h, 22);
  ctx.clip();
  ctx.drawImage(image, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

function loadImage(src, { crossOrigin = null } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
